import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";

const WA = "https://wa.me/5542999587464";

export default function AppDetail() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/apps").then((r) => {
      const found = r.data.find((a) => a.id === id);
      if (found) setApp(found); else setNotFound(true);
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6">
        <p className="font-head font-black text-4xl">App não encontrado</p>
        <Link to="/#produtos" className="font-mono text-xs uppercase tracking-[0.2em] px-6 py-3 border border-white/20 hover:bg-white hover:text-ink transition-colors">Voltar para a loja</Link>
      </div>
    );
  }
  if (!app) return <div className="min-h-screen bg-ink" />;

  const featured = app.id === "custom";

  return (
    <div className="relative">
      <Nav />
      <main className="pt-24 md:pt-32">
        <section className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/#produtos" data-testid="app-detail-back"
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white transition-colors mb-10">
            <ArrowLeft className="w-4 h-4" /> Voltar para a loja
          </Link>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-acid">
                {featured ? "Projeto sob medida" : "App pronto"}
              </span>
              <h1 className="font-head font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] mt-4 mb-6" data-testid="app-detail-title">
                {app.name}
              </h1>
              <p className="text-white/60 text-lg leading-relaxed mb-8">{app.long_description}</p>

              <div className="flex items-baseline gap-3 mb-8">
                <span className="font-head font-black text-4xl md:text-5xl">{app.price}</span>
                {!featured && <span className="font-mono text-xs text-white/40">licença única</span>}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href={WA} target="_blank" rel="noreferrer" data-testid="app-detail-buy"
                   className="inline-flex items-center justify-center gap-3 bg-cobalt text-white px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-ink transition-colors duration-300">
                  {featured ? "Solicitar orçamento" : "Comprar agora"}
                </a>
                <Link to="/#recomendador"
                      className="inline-flex items-center justify-center px-8 py-4 border border-white/20 font-mono text-xs uppercase tracking-[0.2em] hover:border-white transition-colors duration-300">
                  Falar com a IA
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}
              className="relative border border-white/10 overflow-hidden">
              <img src={app.image} alt={app.name} className="w-full h-[280px] md:h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
            </motion.div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">Recursos</span>
          <div className="grid md:grid-cols-2 gap-4 mt-8">
            {app.features.map((f, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="flex items-center gap-4 border border-white/10 bg-surface/40 p-5" data-testid={`feature-${i}`}>
                <Check className="w-5 h-5 text-acid shrink-0" />
                <span className="text-white/80">{f}</span>
              </motion.div>
            ))}
          </div>

          {app.gallery?.length > 0 && (
            <div className="grid md:grid-cols-2 gap-6 mt-16">
              <div className="border border-white/10 overflow-hidden">
                <img src={app.gallery[0]} alt="" className="w-full h-[260px] md:h-[360px] object-cover" />
              </div>
              <div className="flex flex-col justify-center border border-cobalt/30 bg-cobalt/5 p-8 md:p-12">
                <p className="font-head font-bold text-2xl md:text-3xl leading-tight mb-4">Ideal para {app.best_for}.</p>
                <p className="text-white/60 leading-relaxed">{app.tagline} Instalação simples e suporte direto com a nossa equipe.</p>
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
